import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { UsersThree, Trash, PaperPlaneRight, DeviceMobile, ClockClockwise, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { IssuanceSourceBadge } from "../../components/IssuanceSourceBadge";
import { CardIssuanceDrawer } from "../../components/CardIssuanceDrawer";

export function IssuanceLogTab({ rows, reload }) {
  const [sending, setSending] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [transferFor, setTransferFor] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [drawerCardId, setDrawerCardId] = useState(null);
  const isActive = (r) => r.status === "active";

  useEffect(() => {
    api.get("/admin/users").then((r) => setAllUsers(r.data || [])).catch(() => setAllUsers([]));
  }, []);

  const deleteCard = async (row) => {
    setDeleting(row.id);
    try {
      await api.delete(`/cards/${row.id}`);
      toast.success(`Deleted card${row.last4 ? ` •••• ${row.last4}` : ""}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const resendEmail = async (row) => {
    if (!row.receiver_email) return toast.error("No receiver email on file");
    if (!isActive(row)) return toast.error("Card is not active yet");
    setSending(`${row.id}:email`);
    try {
      const r = await api.post(`/cards/${row.id}/email`, { to_email: row.receiver_email });
      if (r.data?.sent) toast.success(`Re-sent email to ${row.receiver_email}`);
      else toast.warning(`Email not delivered: ${r.data?.error || "provider off"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally { setSending(null); }
  };

  const resendMms = async (row) => {
    if (!row.receiver_phone) return toast.error("No receiver phone on file");
    if (!isActive(row)) return toast.error("Card is not active yet");
    setSending(`${row.id}:mms`);
    try {
      const r = await api.post(`/cards/${row.id}/text`, { to_phone: row.receiver_phone });
      if (r.data?.sent) toast.success(`MMS queued to ${r.data.to_phone}`);
      else toast.warning("MMS not sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "MMS failed");
    } finally { setSending(null); }
  };

  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-auto" data-testid="issuance-table">
      <table className="w-full text-sm">
        <thead className="text-left text-[#4A5C50] text-xs uppercase tracking-widest">
          <tr>
            <th className="p-4">Issued</th>
            <th className="p-4">Issuer</th>
            <th className="p-4">Receiver</th>
            <th className="p-4">Last 4</th>
            <th className="p-4 text-right">Source</th>
            <th className="p-4 text-right">Balance</th>
            <th className="p-4">Top-ups</th>
            <th className="p-4">Status</th>
            <th className="p-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-[#4A5C50]">No issuance records yet.</td></tr>}
          {rows.map((r) => {
            const canEmail = isActive(r) && !!r.receiver_email;
            const canMms = isActive(r) && !!r.receiver_phone;
            return (
              <tr key={r.id} className="border-t border-[#E5E0D8]" data-testid={`issuance-row-${r.id}`}>
                <td className="p-4 mono text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-4">{r.issuer_name}</td>
                <td className="p-4">
                  <div>{r.receiver_name}</div>
                  <div className="text-xs text-[#4A5C50] mono">{r.receiver_email}</div>
                  {r.receiver_phone && <div className="text-xs text-[#4A5C50] mono">{r.receiver_phone}</div>}
                </td>
                <td className="p-4 mono">
                  <div>{r.last4 ? `•••• ${r.last4}` : "—"}</div>
                  <div className="mt-1.5">
                    <IssuanceSourceBadge provider={r.provider} network={r.network} size="xs" />
                  </div>
                </td>
                <td className="p-4 mono text-right">${(r.source_amount || 0).toFixed(2)}</td>
                <td className="p-4 mono text-right">${(r.balance || 0).toFixed(2)}</td>
                <td className="p-4">{r.topups_count}</td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${isActive(r) ? "bg-[#184A30]/10 text-[#184A30]" : r.status === "pending_payment" ? "bg-[#D96C4A]/15 text-[#B04E2F]" : "bg-neutral-200 text-neutral-700"}`}>
                    {r.status?.replace("_", " ")}
                  </span>
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  <div className="inline-flex gap-2">
                    <Button size="sm" variant="ghost"
                      className="rounded-lg text-[#4A5C50] hover:text-[#0A1F16] hover:bg-[#184A30]/5"
                      onClick={() => setDrawerCardId(r.id)}
                      data-testid={`view-issuance-events-${r.id}`}
                      title="View live authorizations + transactions for this card">
                      <Eye size={13} className="mr-1" /> Events
                    </Button>
                    <DeliveryHistoryPopover cardId={r.id} />
                    <Button size="sm" variant="outline"
                      className="rounded-lg border-[#184A30] text-[#184A30] hover:bg-[#184A30]/5"
                      onClick={() => resendEmail(r)}
                      disabled={sending === `${r.id}:email` || !canEmail}
                      data-testid={`resend-email-${r.id}`}
                      title={!r.receiver_email ? "No receiver email on file" : !isActive(r) ? "Card not active" : "Re-send card by email"}>
                      <PaperPlaneRight size={13} className="mr-1" />
                      {sending === `${r.id}:email` ? "Sending…" : "Email"}
                    </Button>
                    <Button size="sm" variant="outline"
                      className="rounded-lg border-[#D96C4A] text-[#B04E2F] hover:bg-[#D96C4A]/10"
                      onClick={() => resendMms(r)}
                      disabled={sending === `${r.id}:mms` || !canMms}
                      data-testid={`resend-mms-${r.id}`}
                      title={!r.receiver_phone ? "No receiver phone on file" : !isActive(r) ? "Card not active" : "Re-send card by MMS"}>
                      <DeviceMobile size={13} className="mr-1" />
                      {sending === `${r.id}:mms` ? "Sending…" : "MMS"}
                    </Button>
                    <Button size="sm" variant="outline"
                      className="rounded-lg border-[#CBA35C] text-[#8C6B21] hover:bg-[#CBA35C]/10"
                      onClick={() => setTransferFor(r)}
                      data-testid={`transfer-card-${r.id}`}
                      title={`Transfer this card from ${r.issuer_email || r.issuer_name || "current owner"} to another user`}>
                      <UsersThree size={13} className="mr-1" /> Transfer
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline"
                          className="rounded-lg border-red-500 text-red-600 hover:bg-red-50"
                          disabled={deleting === r.id}
                          data-testid={`delete-issuance-${r.id}`}
                          title="Delete this card and all its records">
                          <Trash size={13} className="mr-1" />
                          {deleting === r.id ? "…" : "Delete"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid={`delete-issuance-confirm-${r.id}`}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete issuance record?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently deletes card {r.last4 ? `•••• ${r.last4}` : "(no last4)"} issued to <b>{r.receiver_name || "—"}</b>,
                            along with all its top-ups, charges, payment transactions, and delivery logs.
                            <br />
                            <span className="text-xs text-[#B04E2F] mt-2 inline-block">This cannot be undone.</span>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => deleteCard(r)}
                            data-testid={`confirm-delete-issuance-${r.id}`}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Dialog open={!!transferFor} onOpenChange={(v) => { if (!v) setTransferFor(null); }}>
        {transferFor && (
          <TransferCardDialog
            card={transferFor}
            users={allUsers}
            onDone={() => { setTransferFor(null); reload && reload(); }}
          />
        )}
      </Dialog>

      <CardIssuanceDrawer
        open={!!drawerCardId}
        cardId={drawerCardId}
        onClose={() => setDrawerCardId(null)}
      />
    </div>
  );
}

function TransferCardDialog({ card, users, onDone }) {
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState(false);
  const options = users.filter((u) => u.id !== card.owner_id);
  const submit = async () => {
    if (!targetId) { toast.error("Pick a target user"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/cards/${card.id}/transfer`, { new_owner_id: targetId });
      toast.success(`Card •••• ${card.last4 || "—"} transferred to ${r.data.new_owner_email}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed"); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-md" data-testid="transfer-card-dialog">
      <DialogHeader>
        <DialogTitle className="serif text-3xl">Transfer card</DialogTitle>
        <DialogDescription className="text-[#4A5C50]">
          Move card <span className="mono text-[#112318]">•••• {card.last4 || "—"}</span> (receiver <span className="mono text-[#112318]">{card.receiver_name || "—"}</span>) to a different owner. The card keeps its balance, history, and receiver details.
        </DialogDescription>
      </DialogHeader>
      <div className="py-2 space-y-4">
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-3 text-sm">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Currently owned by</p>
          <p className="mono mt-1">{card.issuer_name || "—"} · <span className="text-[#4A5C50]">{card.issuer_email || card.owner_id}</span></p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#4A5C50]">New owner</Label>
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger className="mt-2 rounded-xl" data-testid="transfer-target-select"><SelectValue placeholder="Pick a user…" /></SelectTrigger>
            <SelectContent>
              {options.length === 0 && <div className="p-3 text-sm text-[#4A5C50] italic">No other users available.</div>}
              {options.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`transfer-target-${u.email}`}>
                  {u.full_name} · {u.email} <span className="text-[10px] uppercase tracking-widest opacity-60 ml-2">{u.role}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onDone} data-testid="transfer-cancel">Cancel</Button>
        <Button onClick={submit} disabled={busy || !targetId}
          className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white" data-testid="transfer-submit">
          {busy ? "Transferring…" : "Transfer card"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function DeliveryHistoryPopover({ cardId }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (items !== null) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/cards/${cardId}/deliveries`);
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  };
  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline"
          className="rounded-lg border-[#E5E0D8] text-[#4A5C50] hover:bg-[#FBF9F6]"
          data-testid={`history-btn-${cardId}`}
          title="Delivery history">
          <ClockClockwise size={13} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-white border-[#E5E0D8]" data-testid={`history-popover-${cardId}`}>
        <div className="px-4 py-3 border-b border-[#E5E0D8]">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">Delivery history</p>
          <p className="serif text-lg">Last 20 sends</p>
        </div>
        <div className="max-h-64 overflow-auto">
          {loading && <p className="px-4 py-6 text-sm text-[#4A5C50]">Loading…</p>}
          {!loading && items && items.length === 0 && (
            <p className="px-4 py-6 text-sm text-[#4A5C50]">No email or MMS sent yet for this card.</p>
          )}
          {!loading && items && items.map((x, i) => (
            <div key={i} className="px-4 py-3 border-b border-[#E5E0D8] last:border-0 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-[#4A5C50]">
                  {x.kind === "email" ? "Email" : "MMS"}
                </span>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${x.ok ? "bg-[#184A30]/10 text-[#184A30]" : "bg-[#D96C4A]/15 text-[#B04E2F]"}`}>
                  {x.ok ? "ok" : "fail"}
                </span>
              </div>
              <p className="mono text-xs mt-1 truncate">{x.target}</p>
              <p className="text-xs text-[#4A5C50] mono">{new Date(x.ts).toLocaleString()}</p>
              {x.detail && x.detail !== "delivered" && (
                <p className="text-[11px] text-[#4A5C50] mt-1 truncate" title={x.detail}>{x.detail}</p>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
